<template>
    <div class="service-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Services</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="isLoading" />
                <div class="form-wrapper" v-else>
                    <div class="input-container">
                        <app-input 
                            label="Name"
                            placeholder="Name of service"
                            v-model="service.name" 
                            :isValid="!submitted || $v.service.name.required"
                            :error="`Name is required`"
                        />
                    </div>
                    <div class="input-container">
                        <label>Billing Type</label>
                        <div class="billing-type-unit-wrapper">
                            <service-billing-type-dropdown
                                :clientId="service.clientId"
                                v-model="service.serviceBillingType" />                             
                        </div>
                    </div>
                    <div class="input-container">
                        <div class="billing-type-unit-wrapper">
                            <billing-cycle-dropdown
                                :clientId="service.clientId"
                                v-model="service.billingCycle" />                             
                        </div>
                    </div>
                    <div class="input-container" v-if="service.serviceBillingType && service.billingCycle">
                        <label>{{ service.serviceBillingType.display }} per {{ service.billingCycle.name  == 'Monthly' ? 'Month' : 'Year'}}</label>
                        <app-input 
                            :placeholder="'100'"
                            :type="'number'"
                            v-model="service.price" >
                            <div slot="inputIcon">
                                <u1-sales-svg />
                            </div>
                        </app-input>
                    </div>
                </div>
            </div> 
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="save()">Save</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const U1SalesSvg = () => import('@/components/svg/grid/u1-sales-svg');
const BillingCycleDropdown = () => import('@/components/services/billing-cycle-dropdown.vue');
const ServiceBillingTypeDropdown = () => import('@/components/services/service-billing-type-dropdown.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import { required} from 'vuelidate/lib/validators';

export default {
    name: 'service-widget',

    props: {
        widget: {
            type: Object,
            required: true
        }
    },

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'u1-sales-svg': U1SalesSvg,
        'app-input': AppInput,
        'billing-cycle-dropdown': BillingCycleDropdown,
        'service-billing-type-dropdown': ServiceBillingTypeDropdown,
        'loading-screen-svg': LoadingScreenSvg
    },

    data() {
        return {
            submitted: false,
            isLoading: true,
            service: {
                id: this.widget.serviceId,
                clientId: this.widget.clientId,
                name: null,
                serviceBillingType: this.$helpers.getServiceBillingTypes()[0],
                billingCycle: this.$helpers.getBillingCycles()[0],
                price: 0
            }
        }
    },

    validations: {
        service: {
            name: {
                required
            },
        },
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        save() {
            this.submitted = true;
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }
            
            if(this.service.id) {
                this.patch();
            } 
            else {
                this.post();
            }
        },

        patch() {
            this.isLoading = true;

            var changes = [{
                'name': 'Name',
                'value': this.service.name
            }, {
                'name': 'BillingCycle',
                'value': this.service.billingCycle.value
            }, {
                'name': 'ServiceBillingType',
                'value': this.service.serviceBillingType.value
            }, {
                'name': 'Price',
                'value': this.service.price
            }];

            this.$api
                .patch(`/api/Services/${ this.service.id }`, changes)
                .then(response => {
                    this.$helpers.showSuccessNotification('Your service has been successfully updated.');
                    EventBus.$emit('service:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.$helpers.showFailNotification('An error occurred while updating your service.', { error });
                });
        },

        post() {
            this.isLoading = true;

            var service = {
                Name: this.service.name,
                Price: parseInt(this.service.price || 0),
                BillingCycle: this.service.billingCycle.value,
                ServiceBillingType: this.service.serviceBillingType.value,
                ClientId: this.service.clientId,
            };

            this.$api
                .post(`/api/Services`, service)
                .then(response => {
                    this.$helpers.showSuccessNotification('Your service has been successfully created.');
                    EventBus.$emit('service:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.$helpers.showFailNotification('An error occurred while creating your service.', { error });
                });
        },

        getService() {
            this.isLoading = true;

            this.$api
                .get(`/service/Services?$filter=Id eq ${ this.service.id }`)
                .then(response => {
                    this.service.id = response.data.value[0].Id;
                    this.service.name = response.data.value[0].Name;
                    this.service.price = response.data.value[0].Price;
                    this.service.serviceBillingType = this.$helpers.getServiceBillingTypes().find(type => type.name === response.data.value[0].ServiceBillingType);
                    this.service.billingCycle = this.$helpers.getBillingCycles().find(cycle => cycle.value === response.data.value[0].BillingCycle);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while fetching your service.', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }
    },

    created() {
        if (this.service.id) {
            this.getService();
        }
        else {
            this.isLoading = false;
        }
    }
}
</script>

<style>
.service-widget .widget-contents-container .transparent-btn {
    background: transparent;
    color: #877D88;
    border: none;
}

.service-widget .widget-contents-container .transparent-btn:hover {
    background: #E1E0E5;
}

.service-widget .app-input.has-icon .input-container .icon-container {
    right: initial;
    left: 10px;
    top: 8px;
}

.service-widget .app-input.has-icon input {
    padding: 7px 15px 7px 35px;
}

.service-widget .app-input .icon-container path {
    fill: #2ADA2B;
}

.service-widget .input-container {
    margin-bottom: 1em;
}
</style>